import { useCallback } from "react";
import { useInstrumentSetup } from "../contexts/InstrumentSetupContext";
import { useActiveBrokerAccount } from "./useActiveBrokerAccount";
import {
  handleInstrumentSetupAfterSync,
  readInstrumentSetupFromPayload,
  resolveBrokerAccountCodeFromSync,
} from "../utils/instrumentSetupAfterSync";
import type { InstrumentSetupSummary } from "../types/instrumentSetup";

export type RunInstrumentSetupAfterSyncOptions = {
  /** Skip GET /broker/trade-accounts when the caller already refreshed. */
  skipTradeAccountsRefetch?: boolean;
};

export function useRunInstrumentSetupAfterSync() {
  const { openSetup } = useInstrumentSetup();
  const { refetch, activeAccountCode, accounts } = useActiveBrokerAccount();

  return useCallback(
    async (
      payload: unknown,
      explicitAccountCode?: string | null,
      options?: RunInstrumentSetupAfterSyncOptions
    ) => {
      const code = resolveBrokerAccountCodeFromSync(
        explicitAccountCode,
        activeAccountCode,
        payload
      );
      if (!code) return;

      const fromPayload = readInstrumentSetupFromPayload(payload);
      const direct =
        payload && typeof payload === "object"
          ? ((payload as { instrument_setup?: InstrumentSetupSummary }).instrument_setup ??
            (payload as { instrumentSetup?: InstrumentSetupSummary }).instrumentSetup ??
            null)
          : null;
      const instrumentSetup = fromPayload ?? direct;
      const accountRow = accounts.find(
        (a) => a.accountCode === code || a.accountCode?.toLowerCase() === code.toLowerCase()
      );

      await handleInstrumentSetupAfterSync({
        brokerAccountCode: code,
        instrumentSetup,
        instrumentSetupPendingOnAccount: accountRow?.instrumentSetupPending,
        refetchTradeAccounts: refetch,
        skipTradeAccountsRefetch: options?.skipTradeAccountsRefetch,
        openMandatorySetup: (accountCode, setupOptions) =>
          openSetup(accountCode, { mandatory: true, ...setupOptions }),
      });
    },
    [activeAccountCode, accounts, openSetup, refetch]
  );
}
